import type { Pie, Slice, D3Slice, D3Pie, HighlightedSlice } from '../types'
import { Event } from '../types'
import { EntityRegistry } from '../core/entity-registry'
import { EventBus } from '../core/event-bus'
import type { OverlayRenderer } from './overlay-renderer'
import type { ForceNode } from '../utils/force-layout'
import { createPieForceSimulation, getNodeById } from '../utils/force-layout'
import * as PIXI from 'pixi.js'
import { pie as d3PieLayout } from 'd3-shape'
import type { Simulation } from 'd3-force'

export class PieRenderer {
  private readonly registry: EntityRegistry = EntityRegistry.getInstance()
  private overlayRenderer: OverlayRenderer
  private readonly d3Pies: D3Pie[] = []
  private readonly eventBus: EventBus = EventBus.getInstance()
  private forceSimulation: Simulation<ForceNode, undefined> | null = null
  private forceNodes: ForceNode[] = []
  private readonly pieGraphicsMap: Map<string, PIXI.Graphics[]> = new Map()

  public initialize (overlayRenderer: OverlayRenderer, pies: Pie[]): void {
    this.overlayRenderer = overlayRenderer

    pies.forEach(pie => {
      const d3Slices = this.createD3Slices(pie.slices)
      this.d3Pies.push({
        id: pie.id,
        coordinates: pie.coordinates,
        radius: pie.radius,
        slices: d3Slices
      })
    })

    // Create force simulation for pie positioning
    const { simulation, nodes } = createPieForceSimulation(this.d3Pies, this.overlayRenderer.project.bind(this.overlayRenderer))
    this.forceSimulation = simulation
    this.forceNodes = nodes

    // Set up simulation tick handler
    this.forceSimulation.on('tick', () => {
      this.updatePiePositions()
      this.eventBus.publish(Event.REDRAW)
    })

    const pieGraphics = this.createPieGraphics()
    this.overlayRenderer.addGraphics(pieGraphics)

    this.eventBus.subscribe(Event.HIGHLIGHT_SLICES, (slices: HighlightedSlice[]) => {
      this.highlightSlices(slices)
    })
  }

  private createD3Slices (slices: Slice[]): D3Slice[] {
    return d3PieLayout<Slice>()
      .value(d => d.percentage)
      .sort((a, b) => b.nodeCount - a.nodeCount)(slices)
  }

  private createPieGraphics (): PIXI.Graphics[] {
    const graphics: PIXI.Graphics[] = []

    this.d3Pies.forEach(d3Pie => {
      const pieGraphics: PIXI.Graphics[] = []
      if (d3Pie.slices != null) {
        d3Pie.slices.forEach(d3Slice => {
          const sliceGraphic = this.sliceGraphics(d3Pie, d3Slice)
          this.registry.registerPieSlice(d3Pie.id, d3Slice.data.id, sliceGraphic)
          graphics.push(sliceGraphic)
          pieGraphics.push(sliceGraphic)
        })
      }
      this.pieGraphicsMap.set(d3Pie.id, pieGraphics)
    })

    return graphics
  }

  private sliceGraphics (pie: D3Pie, slice: D3Slice): PIXI.Graphics {
    const graphics = new PIXI.Graphics()

    // Get force node position if available, otherwise use original position
    const forceNode = getNodeById(this.forceNodes, pie.id)
    const centerX = forceNode?.x ?? 0
    const centerY = forceNode?.y ?? 0

    // Register pie metadata for collision detection
    this.registry.registerPieMetadata(pie.id, centerX, centerY, pie.radius)

    graphics.beginFill(slice.data.color.int)
    graphics.moveTo(0, 0)
    graphics.arc(
      0,
      0,
      pie.radius,
      slice.startAngle - Math.PI / 2,
      slice.endAngle - Math.PI / 2
    )
    graphics.lineTo(0, 0)
    graphics.endFill()

    // Position will be set by force simulation
    graphics.position.set(centerX, centerY)

    graphics.eventMode = 'static'
    graphics.cursor = 'pointer'

    graphics.on('click', () => this.eventBus.publish(Event.SLICE_CLICKED, { pieId: pie.id, sliceId: slice.data.id }))
    graphics.on('mouseover', () => this.eventBus.publish(Event.SLICE_HOVERED, { pieId: pie.id, sliceId: slice.data.id }))
    graphics.on('mouseout', () => this.eventBus.publish(Event.SLICE_UNHOVERED, { pieId: pie.id, sliceId: slice.data.id }))

    return graphics
  }

  private highlightSlices (slices: HighlightedSlice[]): void {
    const allSliceGraphics = this.registry.getAllPieSliceGraphics()
    if (slices.length === 0) {
      allSliceGraphics.forEach(graphic => {
        graphic.tint = 0xFFFFFF
      })
    } else {
      allSliceGraphics.forEach(graphic => {
        graphic.tint = 0x333333
      })

      slices.forEach(slice => {
        const graphic = this.registry.getPieSlice(slice.pie.id, slice.slice.id)
        if (graphic != null) {
          graphic.tint = 0xFFFFFF
        }
      })
    }

    this.eventBus.publish(Event.REDRAW)
  }

  private updatePiePositions (): void {
    this.forceNodes.forEach(node => {
      const pieGraphics = this.pieGraphicsMap.get(node.id)
      if (pieGraphics != null) {
        pieGraphics.forEach(graphic => {
          graphic.position.set(node.x, node.y)
        })
        // Update registry with new position
        this.registry.registerPieMetadata(node.id, node.x, node.y, node.radius)
      }
    })
    // Notify that pie positions have been updated
    this.eventBus.publish(Event.PIE_POSITIONS_UPDATED)
  }
}
